import { Injectable, Injector, OnDestroy } from '@angular/core';
import Basemap from '@arcgis/core/Basemap';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import GroupLayer from '@arcgis/core/layers/GroupLayer';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import WMSLayer from '@arcgis/core/layers/WMSLayer';
import {
  asapScheduler,
  asyncScheduler,
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  defaultIfEmpty,
  defer,
  distinctUntilChanged,
  EMPTY,
  filter,
  finalize,
  first,
  map,
  merge,
  mergeMap,
  Observable,
  observeOn,
  of,
  ReplaySubject,
  skip,
  skipWhile,
  startWith,
  Subject,
  subscribeOn,
  Subscription,
  take,
  takeUntil,
  tap,
  zip,
} from 'rxjs';
import { LoadingController } from '../../helpers/loading.controller';
import {
  TocBasemapItemConfigLike,
  TocBasemapTypesDefault,
  TocLayerItemConfigLike,
  TocLayerItemConfigType,
  TocMenuItemLike,
  TocMenuItemsDefault,
} from '../../models/toc-config';
import { MainMapService } from './main-map.service';
import { EsriUtils, SublayerTypes } from '../../utils/esri.utils';
import { ObjectUtils } from '../../utils/object.utils';
import { LayersInfoService } from './layers.service';
import { AppConfigService } from '../../services/app-config.service';
import Sublayer from '@arcgis/core/layers/support/Sublayer';
import { AppTrackingService } from '../../services/app-tracking.service';
import { TranslationService } from '@services/translation.service';
import { availableLanguages } from 'translations';

export interface SiiaLayersCreatedEvent {
  mainMapImageLayer?: __esri.MapImageLayer;
  occurrenceLayer?: __esri.MapImageLayer;
  announcementLayer?: __esri.MapImageLayer;
  limitsLayer?: __esri.MapImageLayer;
}

export interface BasemapItemLike {
  id: string;
  imageUrl?: string;
  text?: string;
  basemap: __esri.Basemap;
  visible?: boolean;
  preservePosition?: boolean;
}

export interface TocMenuItemEnableLike extends TocMenuItemLike {
  enabled?: boolean;
}

type LayerType = __esri.Layer | __esri.Sublayer;

const defaultExternalLayerVisible = true;

@Injectable()
export class TocService implements OnDestroy {
  private _openedSubject = new BehaviorSubject<boolean>(false);
  readonly openedChanges = this._openedSubject
    .asObservable()
    .pipe(distinctUntilChanged());

  get opened() {
    return this._openedSubject.getValue();
  }

  set opened(value) {
    this._openedSubject.next(!!value);
  }

  private _menuItemsLoadedSubject = new ReplaySubject<TocMenuItemEnableLike[]>(
    1
  );

  private _layerItemLoadingMap = new WeakMap<
    TocLayerItemConfigLike,
    Observable<boolean>
  >();

  private mapView: __esri.MapView;
  private siiaGroupLayer: __esri.GroupLayer;
  private additionalGroupLayer: __esri.GroupLayer;
  private tocGroupLayer: __esri.GroupLayer;

  private defaultBasemap: __esri.Basemap;
  private basemapSelectedIndex = 0;
  private _basemapSelectedIndexSubject = new BehaviorSubject<number>(
    this.basemapSelectedIndex
  );

  private menuItems: TocMenuItemEnableLike[];

  private basemapList: BasemapItemLike[];
  private _basemapListLoadSubject = new ReplaySubject<BasemapItemLike[]>(1);

  private _siiaLayersCreatedSubject = new Subject<SiiaLayersCreatedEvent>();

  private mapLoadSubscription: Subscription;
  private basemapListLoadSubscription: Subscription;
  private siiaGroupLayerLoadSubscription: Subscription;
  private additionalLayerLoadSubscription: Subscription;
  private externalLayerLoadSubscription: Subscription;

  private menuItemSubscriptionMap = new Map<
    TocMenuItemEnableLike,
    Subscription
  >();
  private menuItemLoadingControllerMap = new WeakMap<
    TocMenuItemEnableLike,
    LoadingController
  >();
  private layerItemListMap = new Map<
    TocMenuItemEnableLike,
    Array<TocLayerItem | LayerType>
  >();

  private layerConfigMap = new WeakMap<
    __esri.Layer | SublayerTypes,
    TocLayerItemConfigLike
  >();

  private showLegendAllowedLayersMap = new WeakMap<
    __esri.Layer | SublayerTypes,
    boolean
  >();

  private heatMapAllowedLayersMap = new WeakMap<
    __esri.Layer | SublayerTypes,
    boolean
  >();

  private loaded = false;
  private destroyed = false;

  constructor(
    private appConfig: AppConfigService,
    private injector: Injector,
    private mainMapService: MainMapService,
    private appTrackingService: AppTrackingService,
    private globalLoadingController: LoadingController,
    private ts: TranslationService
  ) {}

  ngOnDestroy() {
    this.destroy();
  }

  toggle() {
    this.opened = !this.opened;
  }

  open() {
    this.opened = true;
  }

  close() {
    this.opened = false;
  }

  onSiiaLayersCreated() {
    return this._siiaLayersCreatedSubject.asObservable().pipe(first());
  }

  onLoadMenuItems() {
    return this._menuItemsLoadedSubject.asObservable();
  }

  onLoadBasemapItems() {
    return this._basemapListLoadSubject.asObservable();
  }

  onBasemapSelectedIndexChange() {
    return this._basemapSelectedIndexSubject.asObservable();
  }

  getPathLayersByLayerAsText(
    layer: __esri.Layer | SublayerTypes,
    includeMenu = false,
    joinSymbol = ' | '
  ): string {
    return this.getPathLayersByLayerAsTextArr(layer, includeMenu).join(
      joinSymbol
    );
  }

  getPathLayersByLayerAsTextArr(
    layer: __esri.Layer | SublayerTypes,
    includeMenu = false
  ): string[] {
    const { menuItem, layers } = this.getPathLayersByLayer(layer);

    const pathStrArr = layers.map((x) => x.title ?? x.id.toString());

    if (includeMenu && menuItem) {
      pathStrArr.unshift(menuItem?.label);
    }

    return pathStrArr;
  }

  getPathLayersByLayer(layer: __esri.Layer | SublayerTypes): {
    menuItem: TocMenuItemEnableLike;
    layers: Array<__esri.Layer | SublayerTypes>;
  } {
    let menuItem: TocMenuItemEnableLike;
    let layers: Array<__esri.Layer | SublayerTypes> = [];

    const recursiveFn = (
      layer: __esri.Layer | SublayerTypes,
      layerList?: Array<__esri.Layer | SublayerTypes>
    ) => {
      if (!layer) {
        return;
      }

      const auxFn = (layerList: Array<__esri.Layer | SublayerTypes>) => {
        const layerFounded: any = EsriUtils.findRecursiveLayer(
          layerList,
          (x) => {
            return x === layer;
          }
        );

        if (layerFounded) {
          layers.unshift(layerFounded);

          recursiveFn(layerFounded.parent ?? layerFounded.layer, layerList);
          return true;
        }

        return false;
      };

      if (layerList) {
        auxFn(layerList);
        return;
      }

      for (const key of this.layerItemListMap.keys()) {
        const list = this.getLayersByItem(key);

        if (auxFn(list as any)) {
          menuItem = key;
          return;
        }
      }
    };

    recursiveFn(layer);

    return { menuItem, layers };
  }

  getLayerMenuItems() {
    return (
      this.menuItems?.filter(
        (x) => x.name !== TocMenuItemsDefault.BasemapList
      ) ?? []
    );
  }

  getLayerItemsByMenu(item: TocMenuItemLike) {
    return this.layerItemListMap.get(item);
  }

  observeMenuIsLoading(item: TocMenuItemLike) {
    return this.menuItemLoadingControllerMap.get(item)?.changes;
  }

  getLayersByItem(item: TocMenuItemLike) {
    return this.layerItemListMap
      .get(item)
      ?.map((x) => {
        if (x instanceof TocLayerItem) {
          return x.layer;
        }

        return x;
      })
      .filter((x) => !!x);
  }

  getAllLayers() {
    return this.getLayerMenuItems().flatMap(
      (x) => this.getLayersByItem(x) ?? []
    );
  }

  getAllLayerItems() {
    return this.getLayerMenuItems().flatMap(
      (x) => this.getLayerItemsByMenu(x) ?? []
    );
  }

  findLayerMenu(
    predicate: (layer: LayerType, menuItem: TocMenuItemEnableLike) => boolean
  ): { layer: LayerType; menuItem: TocMenuItemEnableLike } {
    for (let menuItem of this.getLayerMenuItems()) {
      const layers = this.getLayersByItem(menuItem);

      const layer = EsriUtils.findRecursiveLayer(layers, (x) => {
        return predicate(x as any, menuItem);
      });

      if (layer) {
        return {
          layer: layer as any,
          menuItem,
        };
      }
    }

    return null;
  }

  findLayerItemByMenuAndLayer(
    menuItem: TocMenuItemLike,
    layer: __esri.Layer | SublayerTypes
  ) {
    const items = (this.getLayerItemsByMenu(menuItem)?.filter((x) => {
      return x instanceof TocLayerItem;
    }) ?? []) as TocLayerItem[];

    const recursiveFn = (items: ReadonlyArray<TocLayerItem>) => {
      for (const item of items) {
        if (item.layer === layer) {
          return item;
        }

        if (item.items?.length) {
          const itemFound = recursiveFn(item.items);
          if (itemFound) {
            return itemFound;
          }
        }
      }

      return null;
    };

    return recursiveFn(items);
  }

  shouldShowLegend(layer: __esri.Layer | SublayerTypes): boolean {
    if (!layer) {
      return false;
    }

    let auxRes: boolean;
    if (this.showLegendAllowedLayersMap.has(layer)) {
      auxRes = this.showLegendAllowedLayersMap.get(layer);
    } else {
      for (const menuItem of this.getLayerMenuItems()) {
        const layerList = this.getLayersByItem(menuItem);
        if (layerList) {
          const found = !!EsriUtils.findRecursiveLayer(
            layerList,
            (x) => x === layer
          );
          if (found) {
            auxRes = menuItem.showLegend !== false;
            break;
          }
        }
      }

      auxRes = auxRes ?? true;
      this.showLegendAllowedLayersMap.set(layer, auxRes);
    }

    return auxRes;
  }

  canShowInHeatmap(layer: __esri.Layer | SublayerTypes): boolean {
    let auxRes: boolean;
    if (this.heatMapAllowedLayersMap.has(layer)) {
      auxRes = this.heatMapAllowedLayersMap.get(layer);
    } else {
      for (const menuItem of this.getLayerMenuItems()) {
        const layerList = this.getLayersByItem(menuItem);
        if (layerList) {
          const found = !!EsriUtils.findRecursiveLayer(
            layerList,
            (x) => x === layer
          );
          if (found) {
            let parentOrLayer = this.layerConfigMap.has(layer)
              ? layer
              : EsriUtils.findParentRecursiveLayer(layer, (x) => {
                  return this.layerConfigMap.has(x);
                });

            if (parentOrLayer) {
              const cfg = this.layerConfigMap.get(parentOrLayer);
              auxRes = cfg?.canShowInHeatmap !== false;
              break;
            }
          }
        }
      }

      auxRes = auxRes ?? true;
      this.heatMapAllowedLayersMap.set(layer, auxRes);
    }

    return auxRes;
  }

  setLayerVisible(
    layerOrItem: __esri.Layer | SublayerTypes | TocLayerItem,
    visible: boolean
  ) {
    visible = !!visible;

    if (layerOrItem.visible === visible) {
      return;
    }

    layerOrItem.visible = visible;

    let value = layerOrItem.title;

    if (!value && 'url' in layerOrItem) {
      value = layerOrItem.url;
    }

    this.appTrackingService.sendLayer(value, visible);
  }

  setRecursivelyLayersVisible(
    layersOrItems: Array<__esri.Layer | SublayerTypes | TocLayerItem>,
    visible: boolean
  ) {
    layersOrItems?.forEach((x) => {
      if (x instanceof TocLayerItem) {
        this.setRecursivelyLayerVisible(x, visible);

        this.setRecursivelyLayersVisible(x.items as any, visible);
      } else {
        EsriUtils.findRecursiveLayer(x, (layer) => {
          this.setLayerVisible(layer, visible);
        });
      }
    });
  }

  setRecursivelyLayerVisible(
    layerOrItem: __esri.Layer | SublayerTypes | TocLayerItem,
    visible: boolean
  ) {
    if (layerOrItem instanceof TocLayerItem) {
      this.setLayerVisible(layerOrItem, visible);

      if (layerOrItem.layer) {
        EsriUtils.findRecursiveLayer(layerOrItem.layer, (layer) => {
          this.setLayerVisible(layer, visible);
        });
      }

      this.setRecursivelyLayersVisible(layerOrItem.items as any, visible);
    } else {
      EsriUtils.findRecursiveLayer(layerOrItem, (layer) => {
        this.setLayerVisible(layer, visible);
      });
    }
  }

  load(
    mapView: __esri.MapView,
    tocGroupLayer?: __esri.GroupLayer,
    siiaGroupLayer?: __esri.GroupLayer,
    additionalGroupLayer?: __esri.GroupLayer
  ) {
    if (this.loaded || this.destroyed) {
      return;
    }

    this.loaded = true;

    this.mapView = mapView;
    this.siiaGroupLayer = siiaGroupLayer;
    this.additionalGroupLayer = additionalGroupLayer;
    this.tocGroupLayer = tocGroupLayer;

    this.setup();
  }

  destroy() {
    if (this.destroyed) {
      return;
    }

    this.destroyed = true;

    this.mapLoadSubscription?.unsubscribe();
    this.basemapListLoadSubscription?.unsubscribe();
    this.siiaGroupLayerLoadSubscription?.unsubscribe();
    this.externalLayerLoadSubscription?.unsubscribe();

    for (const sub of Array.from(this.menuItemSubscriptionMap.values())) {
      sub.unsubscribe();
    }

    this.menuItemSubscriptionMap.clear();
    this.layerItemListMap.clear();
  }

  private setup() {
    const thisRef = new WeakRef(this);

    const defaultMenuItems: TocMenuItemEnableLike[] = [
      {
        icon: 'assets/icons/map.svg',
        label: this.ts.getCurrentLanguage()['base-maps'],
        name: TocMenuItemsDefault.BasemapList,
        get enabled() {
          return thisRef.deref().basemapSelectedIndex > -1;
        },
      },
      {
        icon: 'assets/icons/layer-group.svg',
        label: this.ts.getCurrentLanguage().themes,
        name: TocMenuItemsDefault.Themes,
      },
      {
        icon: 'assets/icons/layer-group.svg',
        label: this.ts.getCurrentLanguage()['external-layers'],
        name: TocMenuItemsDefault.AdditionalLayers,
      },
    ];

    let menuItems = this.appConfig.toc?.items
      ?.map((item) => {
        item = ObjectUtils.clone(item);
        const defaultItem = defaultMenuItems.find((x) => x.name === item.name);
        if (defaultItem) {
          item = Object.assign(ObjectUtils.clone(defaultItem), item);
        }

        return item;
      })
      ?.filter((x) => !!x);

    if (menuItems) {
      const defaultMenuItemsAux = defaultMenuItems.filter((item) => {
        return !menuItems.some(
          (x) => x.name === item.name && x.preservePosition === false
        );
      });

      defaultMenuItemsAux.forEach((item, index, arr) => {
        const itemAuxIndex = menuItems.findIndex(
          (x) =>
            x.name === item.name &&
            (x.preservePosition === undefined || x.preservePosition === true)
        );

        if (itemAuxIndex > -1) {
          arr[index] = menuItems[itemAuxIndex];
          menuItems.splice(itemAuxIndex, 1);
        }
      });

      menuItems = defaultMenuItemsAux.concat(menuItems);
    }

    this.menuItems =
      menuItems?.filter((x) => x.visible !== false) ?? defaultMenuItems;

    this.menuItems
      ?.filter((x) => x.name !== TocMenuItemsDefault.BasemapList)
      ?.forEach((item) => {
        Object.defineProperty(item, 'enabled', {
          get: () => {
            return !!thisRef
              .deref()
              .getLayersByItem(item)
              ?.some((layer) => layer.visible);
          },
        });
      });

    this.menuItems?.forEach((item) => {
      if (typeof item.visible !== 'boolean') {
        item.visible = true;
      }
    });

    this._menuItemsLoadedSubject.next(this.menuItems);

    this.handleMapView();
    this.handleSiiaGroupLayer();
    this.handleAdditionalGroupLayer();
    this.handleExternalGroupLayer();
  }

  private handleMapView() {
    this.mapLoadSubscription?.unsubscribe();
    if (!this.mapView) {
      return;
    }

    this.mapLoadSubscription = EsriUtils.when(this.mapView).subscribe(() => {
      this.defaultBasemap = this.mapView.map.basemap;

      this.handleBasemapItems();
    });
  }

  private handleBasemapItems() {
    this.basemapListLoadSubscription?.unsubscribe();

    const defaultBasemapItemList: BasemapItemLike[] = [this.defaultBasemap]
      .filter((x) => !!x)
      .map<BasemapItemLike>((basemap) => {
        let title = basemap.title;

        if (
          title === 'Híbrido de Imagens Firefly' &&
          this.ts.getCurrentLanguageCode() === availableLanguages.en
        ) {
          title = 'Firefly Imagery Hybrid';
        }

        return {
          id:
            this.defaultBasemap === basemap
              ? TocBasemapTypesDefault.Default
              : basemap.id,
          imageUrl: basemap.thumbnailUrl,
          text: title,
          basemap,
        };
      });

    [
      {
        id: 'gray-vector',
        label: this.ts.getCurrentLanguage()['light-gray-canvas'],
      },
      {
        id: 'dark-gray-vector',
        label: this.ts.getCurrentLanguage()['dark-gray-canvas'],
      },
      { id: 'hybrid', label: this.ts.getCurrentLanguage()['imagery-hybrid'] },
      {
        id: 'national-geographic',
        label: this.ts.getCurrentLanguage()['national-geographic-map'],
      },
      {
        id: 'streets-navigation-vector',
        label: this.ts.getCurrentLanguage()['navigation-map'],
      },
      { id: 'oceans', label: this.ts.getCurrentLanguage()['oceans'] },
      { id: 'streets-vector', label: this.ts.getCurrentLanguage()['streets'] },
      {
        id: 'terrain',
        label: this.ts.getCurrentLanguage()['terrain-with-labels'],
      },
      { id: 'topo-vector', label: this.ts.getCurrentLanguage()['topographic'] },
      { id: 'osm', label: 'Open Street Map' },
    ].forEach(({ id, label }) => {
      const basemap = Basemap.fromId(id);
      if (!basemap) {
        return;
      }

      defaultBasemapItemList.push({
        id: basemap.id,
        text: label,
        basemap,
      });
    });

    const mainDefaultItem = defaultBasemapItemList[0];

    let basemapItemList: BasemapItemLike[] = (
      this.menuItems?.find((x) => x.name === TocMenuItemsDefault.BasemapList)
        ?.data as any[]
    )
      ?.map((basemapCfg: string | TocBasemapItemConfigLike) => {
        const isValueString = typeof basemapCfg === 'string';
        const basemapId = isValueString ? basemapCfg : basemapCfg.id;
        const defaultBasemapItem =
          basemapId === TocBasemapTypesDefault.Default
            ? mainDefaultItem
            : defaultBasemapItemList.find((x) => x.id === basemapId);

        let basemapItem: BasemapItemLike;

        if (defaultBasemapItem) {
          basemapItem = defaultBasemapItem;
        } else {
          const basemap =
            !isValueString && basemapCfg.isPortalItem
              ? new Basemap({
                  id: basemapId,
                  portalItem: {
                    id: basemapId,
                  },
                })
              : Basemap.fromId(basemapId);
          basemapItem = {
            id: basemap.id,
            basemap,
          };
        }

        if (!isValueString) {
          if (typeof basemapCfg.imageUrl === 'string') {
            basemapItem.imageUrl = basemapCfg.imageUrl;
          }

          if (typeof basemapCfg.title === 'string') {
            basemapItem.text = basemapCfg.title;
          }

          if (typeof basemapCfg.visible === 'boolean') {
            basemapItem.visible = basemapCfg.visible;
          }

          if (typeof basemapCfg.preservePosition === 'boolean') {
            basemapItem.preservePosition = basemapCfg.preservePosition;
          }
        }

        return basemapItem;
      })
      ?.filter((x) => !!x);

    if (basemapItemList) {
      const defaultBasemapItemListAux = defaultBasemapItemList.filter(
        (item) => {
          return !basemapItemList.some(
            (x) => x.id === item.id && x.preservePosition === false
          );
        }
      );

      defaultBasemapItemListAux.forEach((item, index, arr) => {
        const itemAuxIndex = basemapItemList.findIndex(
          (x) =>
            x.id === item.id &&
            (x.preservePosition === undefined || x.preservePosition === true)
        );

        if (itemAuxIndex > -1) {
          arr[index] = basemapItemList[itemAuxIndex];
          basemapItemList.splice(itemAuxIndex, 1);
        }
      });

      basemapItemList = defaultBasemapItemListAux.concat(basemapItemList);
    }

    basemapItemList =
      basemapItemList?.filter((x) => x.visible !== false) ??
      defaultBasemapItemList;

    this.basemapListLoadSubscription = EsriUtils.load(
      basemapItemList.map((x) => x.basemap)
    ).subscribe(() => {
      this.basemapList = basemapItemList.map<BasemapItemLike>((item) => {
        const basemap = item.basemap;

        const referenceLayers = basemap.referenceLayers?.toArray();
        basemap.referenceLayers?.removeAll();

        if (referenceLayers?.length) {
          basemap.baseLayers?.addMany(referenceLayers);
        }

        return {
          id: item.id,
          imageUrl: item.imageUrl ?? basemap.thumbnailUrl,
          text: item.text ?? basemap.title,
          basemap,
        };
      });

      this._basemapListLoadSubject.next(this.basemapList);

      this.basemapListLoadSubscription = this.mainMapService.onBasemapIdChanged
        .pipe(startWith(this.mainMapService.getBasemapId()))
        .subscribe((basemapId) => {
          const index = this.basemapList.findIndex((x) => x.id === basemapId);

          if (this.basemapSelectedIndex !== index) {
            this.basemapSelectedIndex = index;
            this._basemapSelectedIndexSubject.next(this.basemapSelectedIndex);

            this.mapView.map.basemap =
              this.basemapList[this.basemapSelectedIndex]?.basemap;
          }
        });
    });
  }

  private handleSiiaGroupLayer() {
    this.siiaGroupLayerLoadSubscription?.unsubscribe();

    const item = this.menuItems.find(
      (x) => x.name === TocMenuItemsDefault.Themes
    );
    if (item) {
      if (this.siiaGroupLayer) {
        const registerLayerItem = (cfg: TocLayerItemConfigLike | string) => {
          return this.registerLayerItem(item, cfg, this.siiaGroupLayer);
        };

        const createdEvent: SiiaLayersCreatedEvent = {};

        const subscription = combineLatest([
          registerLayerItem({
            type: TocLayerItemConfigType.MapImage,
            url: '~limitsService',
            visible: true,
            canShowInHeatmap: false,
          }).pipe(
            tap((layer) => {
              createdEvent.limitsLayer = layer as any;
            })
          ),
          registerLayerItem({
            type: TocLayerItemConfigType.MapImage,
            url: '~occurrenceService',
            visible: false,
            canShowInHeatmap: false,
          }).pipe(
            tap((layer) => {
              createdEvent.occurrenceLayer = layer as any;
            })
          ),
          registerLayerItem({
            type: TocLayerItemConfigType.MapImage,
            url: '~announcementTOCService',
            visible: false,
            canShowInHeatmap: false,
          }).pipe(
            tap((layer) => {
              createdEvent.announcementLayer = layer as any;
            })
          ),
          registerLayerItem({
            type: TocLayerItemConfigType.MapImage,
            url: '~registerObjectsService',
            visible: true,
            canShowInHeatmap: false,
          }).pipe(
            tap((layer) => {
              createdEvent.mainMapImageLayer = layer as any;
            })
          ),
        ]).subscribe(() => {
          this._siiaLayersCreatedSubject.next(createdEvent);
        });

        this.siiaGroupLayerLoadSubscription = subscription;
      }
    }
  }

  private handleAdditionalGroupLayer() {
    this.additionalLayerLoadSubscription?.unsubscribe();
    const item = this.menuItems.find(
      (x) => x.name === TocMenuItemsDefault.AdditionalLayers
    );
    if (item) {
      const subscription = new Subscription();

      if (this.additionalGroupLayer) {
        const hasItems = () => !!this.additionalGroupLayer.layers.length;
        subscription.add(
          EsriUtils.on(this.additionalGroupLayer.layers, 'change')
            .pipe(map(hasItems), startWith(hasItems()), distinctUntilChanged())
            .subscribe((visible) => {
              item.visible = visible;
            })
        );
      }

      let layers = this.additionalGroupLayer ? [this.additionalGroupLayer] : [];

      this.globalLoadingController.show();
      subscription.add(
        EsriUtils.loadAllLayers(layers)
          .pipe(
            finalize(() => {
              this.globalLoadingController.hide();
            })
          )
          .subscribe(() => {
            this.layerItemListMap.set(item, layers);
          })
      );

      subscription.add(() => {
        this.layerItemListMap.set(item, []);
      });

      this.additionalLayerLoadSubscription = subscription;
    }
  }

  private handleExternalGroupLayer() {
    this.externalLayerLoadSubscription?.unsubscribe();
    if (!this.tocGroupLayer) {
      return;
    }

    const { tocGroupLayer: externalGroupLayer } = this;

    const subscription = new Subscription();

    const defaultNames = Object.values(TocMenuItemsDefault);
    const items = this.menuItems
      .filter((x) => !defaultNames.includes(x.name as any))
      .reverse();
    if (items.length) {
      items.forEach((menuItem) => {
        const sub = new Subscription();
        subscription.add(sub);

        const groupLayer = new GroupLayer();
        externalGroupLayer.add(groupLayer);

        menuItem.data?.forEach((item, index) => {
          sub.add(
            this.registerLayerItem(
              menuItem,
              item as any,
              groupLayer,
              index
            ).subscribe()
          );
        });

        sub.add(() => {
          externalGroupLayer.remove(groupLayer);
        });
      });
    }

    this.externalLayerLoadSubscription = subscription;
  }

  private registerLayerItem(
    menu: TocMenuItemLike,
    cfg: TocLayerItemConfigLike | string,
    layersMixin: __esri.LayersMixin,
    index?: number
  ) {
    let menuSubscription = this.menuItemSubscriptionMap.get(menu);
    if (!menuSubscription) {
      menuSubscription = new Subscription();
      this.menuItemSubscriptionMap.set(menu, menuSubscription);
    }

    let menuLoadingController = this.menuItemLoadingControllerMap.get(menu);
    if (!menuLoadingController) {
      menuLoadingController = new LoadingController();
      this.menuItemLoadingControllerMap.set(menu, menuLoadingController);
    }

    let list = this.layerItemListMap.get(menu);
    if (!list) {
      list = [];
      this.layerItemListMap.set(menu, list);
    }

    return new Observable<__esri.Layer | SublayerTypes>((subscriber) => {
      menuSubscription.add(() => {
        subscriber.complete();
        subscriber.unsubscribe();
      });

      if (subscriber.closed) {
        return;
      }

      const item = new TocLayerItem(cfg, this.injector);
      list.push(item);

      subscriber.add(
        item
          .attach({
            layersMixin,
            index,
            menuLoadingController,
          })
          .subscribe()
      );

      subscriber.add(
        item.whenLayerLoaded.subscribe((layer) => {
          subscriber.next(layer);
        })
      );

      subscriber.add(() => {
        const listIndex = list.indexOf(item);
        if (listIndex > -1) {
          list.splice(listIndex, 1);
        }

        item.dispose();
      });

      subscriber.add(
        item.onDispose.subscribe(() => {
          subscriber.complete();
        })
      );
    });
  }
}

export class TocLayerItem {
  readonly type: TocLayerItemConfigType;

  private get _layerId() {
    return this.layer?.id?.toString();
  }

  private _title: string;

  get title() {
    return this._title ?? this.layer?.title ?? this._layerId;
  }

  readonly url?: string;

  private loadingController = new LoadingController();

  private _loadingStartedSubject = new BehaviorSubject(false);
  private _layerLoadedSubject = new ReplaySubject<__esri.Layer>(1);

  private _visible = false;
  private _visibleSubject = new Subject<boolean>();
  readonly visibleChanges = this._visibleSubject.pipe(distinctUntilChanged());

  get visible() {
    return !!this._visible;
  }

  set visible(value) {
    value = !!value;
    if (value) {
      this._loadingStartedSubject.next(true);
      this._loadingStartedSubject.complete();
    }

    this._visible = value;
    this._visibleSubject.next(value);
  }

  layer: __esri.Layer;

  readonly whenLayerLoaded = this._layerLoadedSubject.pipe(take(1));

  readonly isLoading = this.loadingController.changes;

  readonly config: TocLayerItemConfigLike;

  private _subscription: Subscription;

  readonly onDispose: Observable<any>;

  readonly deferLoadMode: boolean;

  private _itemsSubject = new BehaviorSubject<TocLayerItem[]>(null);

  get items(): ReadonlyArray<TocLayerItem> {
    return this._itemsSubject.getValue();
  }

  readonly itemsChanged = this._itemsSubject.pipe(debounceTime(0));

  private _parentRef: WeakRef<TocLayerItem>;
  get parent() {
    return this._parentRef?.deref();
  }

  get root() {
    let parent = this.parent;

    while (parent?.parent) {
      parent = parent.parent;
    }

    return parent;
  }

  private _disposed = false;
  get disposed() {
    return this._disposed;
  }

  constructor(
    cfg: TocLayerItemConfigLike | string,
    private injector: Injector,
    parent?: TocLayerItem
  ) {
    if (parent) {
      this._parentRef = new WeakRef(parent);
    }

    this._subscription = new Subscription();

    let cfgAux: TocLayerItemConfigLike;
    if (typeof cfg === 'string') {
      cfgAux = {
        type: TocLayerItemConfigType.Feature,
        url: cfg,
      };
    } else {
      cfgAux = cfg;
    }

    this.config = cfgAux;
    this.type = cfgAux?.type;

    let url = cfgAux?.url;
    url = this.injector.get(AppConfigService).normalizeUrl(url);
    this.url = url;

    if (cfgAux) {
      cfgAux.url = url;
    }

    const hasTitle = typeof cfgAux?.title === 'string';
    if (hasTitle) {
      this._title = cfgAux.title;
    }

    const visible = cfgAux?.visible ?? defaultExternalLayerVisible;

    const deferLoad = !visible && !!cfgAux?.deferLoad;
    this.deferLoadMode = deferLoad;

    let layerTitleObservable: Observable<string> = hasTitle
      ? of(cfgAux.title)
      : null;

    let layerCreatedObservable: Observable<__esri.Layer>;

    if (
      cfgAux?.type === TocLayerItemConfigType.MapImage ||
      cfgAux?.type === TocLayerItemConfigType.WMS
    ) {
      if (!hasTitle) {
        layerTitleObservable = this.getLayerNameByUrl(url);
      }

      layerCreatedObservable = defer(() => {
        let layer: MapImageLayer | WMSLayer;
        let layerWithSublayersObservable: Observable<MapImageLayer | WMSLayer>;

        switch (cfgAux.type) {
          case TocLayerItemConfigType.MapImage:
            layer = new MapImageLayer({ url, visible });

            layerWithSublayersObservable = EsriUtils.loadAllLayers(layer).pipe(
              map(() => {
                return layer;
              })
            );

            break;
          case TocLayerItemConfigType.WMS:
            layer = new WMSLayer({
              url,
              visible,
            });

            layerWithSublayersObservable = EsriUtils.loadAllLayers(layer).pipe(
              map(() => {
                return layer;
              })
            );
            break;
        }

        if (cfgAux?.excludeSublayerIds?.length) {
          layerWithSublayersObservable = layerWithSublayersObservable.pipe(
            tap((layer) => {
              EsriUtils.removeRecursiveLayerRangeFrom(layer, (layerAux) => {
                return cfgAux.excludeSublayerIds.includes(layerAux.id as any);
              });
            })
          );
        }

        if (cfgAux?.excludeSublayerNames?.length) {
          layerWithSublayersObservable = layerWithSublayersObservable.pipe(
            tap((layer) => {
              EsriUtils.removeRecursiveLayerRangeFrom(layer, (layerAux) => {
                return cfgAux.excludeSublayerNames.includes(
                  (layerAux as __esri.WMSSublayer).name as any
                );
              });
            })
          );
        }

        if (cfgAux?.sublayers?.length) {
          layerWithSublayersObservable = layerWithSublayersObservable.pipe(
            tap((layer) => {
              const sublayersFromIds = cfgAux.sublayers
                .map((x) => {
                  return (x as any)?.id ?? x;
                })
                .filter((x) => typeof x === 'number')
                .map((id) => {
                  return EsriUtils.findRecursiveLayer(
                    layer,
                    (layerAux) => layerAux.id === id
                  );
                })
                .filter((x) => !!x);

              const sublayersFromNames = cfgAux.sublayers
                .map((x) => {
                  return (x as any)?.name ?? x;
                })
                .filter((x) => typeof x === 'string')
                .map((name) => {
                  return EsriUtils.findRecursiveLayer(
                    layer,
                    (layerAux) => (layerAux as __esri.WMSSublayer).name === name
                  );
                })
                .filter((x) => !!x);

              layer.sublayers.removeAll();
              layer.sublayers.addMany(sublayersFromIds as any);
              layer.sublayers.addMany(sublayersFromNames as any);

              cfgAux.sublayers.forEach((x) => {
                if (typeof x === 'string' || typeof x === 'number') {
                  return;
                }

                let layerFound: __esri.WMSSublayer | __esri.Sublayer;

                const id = (x as any).id as number;
                if (typeof id === 'number') {
                  layerFound = EsriUtils.findRecursiveLayer(
                    [layer],
                    (layerAux) => layerAux.id === id
                  ) as any;
                } else {
                  const name = (x as any).name as string;
                  if (typeof name === 'string') {
                    layerFound = EsriUtils.findRecursiveLayer(
                      [layer],
                      (layerAux) =>
                        (layerAux as __esri.WMSSublayer).name === name
                    ) as any;
                  }
                }

                if (layerFound) {
                  if (typeof x.title === 'string') {
                    layerFound.title = x.title;
                  }

                  if (typeof x.visible === 'boolean') {
                    layerFound.visible = x.visible;
                  }

                  if (typeof x.maxScale === 'number') {
                    layerFound.maxScale = x.maxScale;
                  }

                  if (typeof x.minScale === 'number') {
                    layerFound.minScale = x.minScale;
                  }
                }
              });
            })
          );
        }

        return layerWithSublayersObservable;
      });
    } else {
      switch (cfgAux?.type) {
        case TocLayerItemConfigType.Group:
          layerCreatedObservable = defer(() => {
            const groupLayer = new GroupLayer({ visible });

            this.registerSubItems(groupLayer, cfgAux?.layers);

            return of(groupLayer);
          });

          break;
        case TocLayerItemConfigType.Feature:
        default:
          layerCreatedObservable = defer(() => {
            return of(
              new FeatureLayer({
                url,
                visible,
              })
            );
          });
          break;
      }

      layerCreatedObservable = layerCreatedObservable.pipe(
        mergeMap((layer) => {
          return EsriUtils.load(layer).pipe(map(() => layer));
        })
      );
    }

    layerCreatedObservable = layerCreatedObservable.pipe(
      mergeMap((layer) => {
        if (!layer) {
          return of(layer);
        }

        const allSubLayers = EsriUtils.getDeeperLayers(layer);
        if (!allSubLayers?.length) {
          return of(layer);
        }

        const observables = allSubLayers.map((layer) => {
          const isSublayer = layer instanceof Sublayer;
          if (!isSublayer && !(layer instanceof FeatureLayer)) {
            return of(null);
          }

          let url: string;
          if (isSublayer) {
            const mainLayer = layer.layer;
            url = EsriUtils.resolveLayerUrl(mainLayer.url, layer.id);
          } else {
            url = layer.url;
          }

          return this.injector
            .get(LayersInfoService)
            .getPopupTemplateFromPortalItem(url)
            .pipe(
              tap((popupTemplate) => {
                if (popupTemplate && !layer.popupTemplate) {
                  layer.popupTemplate = popupTemplate;
                }
              })
            );
        });

        return zip(...observables).pipe(map(() => layer));
      })
    );

    this._subscription.add(
      this._loadingStartedSubject
        .pipe(
          filter((x) => x),
          take(1),
          mergeMap(() => {
            this.loadingController.show();

            return zip(
              layerCreatedObservable,
              layerTitleObservable?.pipe(
                catchError((e) => {
                  return of(null);
                })
              ) ?? of(null)
            ).pipe(
              subscribeOn(asapScheduler),
              map(([layer, layerTitle]) => {
                if (!layer) {
                  return null;
                }

                if (typeof layerTitle === 'string') {
                  layer.title = layerTitle;
                }

                if (
                  'maxScale' in layer &&
                  typeof cfgAux?.maxScale === 'number'
                ) {
                  (layer as any).maxScale = cfgAux.maxScale;
                }

                if (
                  'minScale' in layer &&
                  typeof cfgAux?.minScale === 'number'
                ) {
                  (layer as any).minScale = cfgAux.minScale;
                }

                const disablePopup = (layer) => {
                  if ((layer as __esri.FeatureLayer).popupEnabled === true) {
                    (layer as __esri.FeatureLayer).popupEnabled = false;
                  }
                };

                disablePopup(layer);

                EsriUtils.findRecursiveLayer(layer, (sublayer) => {
                  disablePopup(sublayer);
                });

                if (this.visible && !layer.visible) {
                  layer.visible = this.visible;
                }

                return layer;
              }),
              catchError((e) => {
                this._title = e?.toString();

                return of(null);
              }),
              finalize(() => {
                this.loadingController.hide();
              })
            );
          })
        )
        .subscribe((layer) => {
          this.layer = layer;

          if (layer) {
            layer.visible = this.visible;

            this._subscription.add(
              this.visibleChanges.subscribe((visible) => {
                layer.visible = visible;
              })
            );

            this._subscription.add(
              this.watchVisibleLayer(layer).subscribe((visible) => {
                this.visible = visible;
              })
            );

            if (this.type !== TocLayerItemConfigType.Group) {
              EsriUtils.findRecursiveLayer(layer, (x) => {
                if (layer !== x) {
                  this._subscription.add(this.syncLayerVisible(x).subscribe());
                }
              });
            }
          }

          this._layerLoadedSubject.next(layer);
          this._layerLoadedSubject.complete();
        })
    );

    const onDisposeSubject = new Subject<void>();
    this.onDispose = onDisposeSubject.asObservable();
    this._subscription.add(() => {
      onDisposeSubject.next();
      onDisposeSubject.complete();
    });

    if (visible) {
      this.visible = visible;
    }

    this._subscription.add(this.syncLayerVisible(this).subscribe());

    if (!deferLoad) {
      this._loadingStartedSubject.next(true);
      this._loadingStartedSubject.complete();
    }
  }

  dispose() {
    if (this._disposed) {
      return;
    }

    this._disposed = true;

    this._subscription?.unsubscribe();

    [
      this._loadingStartedSubject,
      this._layerLoadedSubject,
      this._itemsSubject,
    ].forEach((x) => {
      x.complete();
      x.unsubscribe();
    });

    EsriUtils.destroyLayersRecursive(this.layer);
  }

  attach({
    layersMixin,
    index,
    menuLoadingController,
    globalMode,
  }: {
    layersMixin: __esri.LayersMixin;
    index?: number;
    menuLoadingController?: LoadingController;
    globalMode?: boolean;
  }) {
    return new Observable<any>((subscriber) => {
      const globalLoadingController = this.injector.get(LoadingController);

      globalMode ??= !this.deferLoadMode;

      subscriber.add(
        this.whenLayerLoaded.subscribe((layer) => {
          if (layer) {
            layersMixin.add(layer, index);
          }
        })
      );

      subscriber.add(
        this.isLoading.pipe(skipWhile((x) => !x)).subscribe((value) => {
          if (value) {
            menuLoadingController?.show();

            if (globalMode) {
              globalLoadingController.show();
            }
          } else {
            menuLoadingController?.hide();

            if (globalMode) {
              globalLoadingController.hide();
            }
          }
        })
      );

      subscriber.add(() => {
        if (this.layer) {
          layersMixin.remove(this.layer);
        }
      });

      subscriber.add(
        this.onDispose.subscribe(() => {
          subscriber.complete();
        })
      );
    });
  }

  private registerSubItems(
    groupLayer: GroupLayer,
    configs?: TocLayerItemConfigLike[]
  ) {
    if (!configs?.length) {
      return;
    }

    const addItem = (item: TocLayerItem) => {
      let list = this._itemsSubject.getValue();
      if (!list) {
        list = [];
      }

      list.push(item);
      this._itemsSubject.next(list);
    };

    const removeItem = (item: TocLayerItem) => {
      const list = this._itemsSubject.getValue();
      if (!list) {
        return;
      }

      const listIndex = list.indexOf(item);
      if (listIndex > -1) {
        list.splice(listIndex, 1);
        this._itemsSubject.next(list);
      }
    };

    configs.forEach((cfg, index) => {
      const sub = new Subscription();
      this._subscription.add(sub);

      const item = new TocLayerItem(cfg, this.injector, this);
      addItem(item);

      sub.add(
        item
          .attach({
            layersMixin: groupLayer,
            index,
            menuLoadingController: this.loadingController,
            globalMode: false,
          })
          .subscribe()
      );

      sub.add(() => {
        removeItem(item);
      });

      sub.add(
        item.onDispose.subscribe(() => {
          sub.unsubscribe();
        })
      );

      sub.add(() => {
        item.dispose();
      });
    });
  }

  private _getKey(layerOrItem: __esri.Layer | SublayerTypes | TocLayerItem) {
    let key: string;

    if (layerOrItem instanceof TocLayerItem) {
      if (layerOrItem.url) {
        key = layerOrItem.url;
      } else if (
        layerOrItem.title &&
        layerOrItem._layerId !== layerOrItem.title
      ) {
        key = `${layerOrItem.type}::${layerOrItem.title}`;
      }
    } else {
      const layerAny = layerOrItem as any;
      let url = layerAny.url as string;
      if (url) {
        const layerId =
          typeof layerAny.id === 'number' ? layerAny.id : layerAny.layerId;
        url = EsriUtils.resolveLayerUrl(url, layerId);
        key = url;
      } else if (layerAny.name) {
        key = `${layerAny.type ?? 'generic'}::${layerAny.name}`;
      } else if (layerAny.title) {
        key = `${layerAny.type ?? 'generic'}::${layerAny.title}`;
      }
    }

    return key;
  }

  private syncLayerVisible(
    layerOrItem: __esri.Layer | SublayerTypes | TocLayerItem
  ): Observable<any> {
    const key = this._getKey(layerOrItem);

    if (!key) {
      return EMPTY;
    }

    const mainMapService = this.injector.get(MainMapService);

    return new Observable<boolean>((subscriber) => {
      subscriber.add(
        mainMapService.onLayerVisibleListChanges
          .pipe(
            map((list) => {
              return list.find((x) => x.layer === key);
            }),
            filter((x) => !!x),
            map((x) => x.visible),
            startWith(mainMapService.isLayerVisible(key))
          )
          .subscribe((visible) => {
            if (typeof visible === 'boolean') {
              layerOrItem.visible = visible;
            }
          })
      );

      let observable: Observable<boolean>;

      if (layerOrItem instanceof TocLayerItem) {
        observable = layerOrItem.visibleChanges;
      } else {
        observable = this.watchVisibleLayer(layerOrItem);
      }

      subscriber.add(
        observable.subscribe((visible) => {
          mainMapService.changeLayerVisible(key, visible);

          subscriber.next(visible);
        })
      );
    });
  }

  private watchVisibleLayer(layer: __esri.Layer | SublayerTypes) {
    return EsriUtils.watch<boolean>(layer, 'visible').pipe(
      map(({ newValue }) => {
        return newValue;
      })
    );
  }

  private getLayerNameByUrl(url: string) {
    return this.injector.get(LayersInfoService).getLayerTitle(url);
  }
}
